import axios from 'axios'
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { keepWhileOnlyFilterChanges, skyProductURL, skyQueryKey } from './skyEndpoints'
import type { CulturalRelevanceResult } from '../../components/social-media/utils'
import type { LabelQuery } from '../../components/social-media/types'

/**
 * The `requireYes` variables of Sky's meaningful-sentiment rule: a comment
 * counts as meaningful only if it is BOTH Sky-relevant and Sky-attributed.
 *
 * Duplicated from useSocialMedia deliberately rather than imported. That hook
 * is the customer/job dashboard's 770-line engine; importing one constant out
 * of it would tie this route group's lifetime to it, and the rule is Sky's
 * business definition, which belongs next to the Sky routes that apply it.
 */
const MEANINGFUL_SENTIMENT_REQUIRE_YES = ['skyrelevant', 'skyattributed']

export interface UseSkyCulturalRelevanceResult {
  culturalRelevance: CulturalRelevanceResult | null
  isLoading: boolean
  /** The previous filter's answer is on screen while the new one loads. */
  isUpdating: boolean
  error: Error | null
}

/**
 * NormComp for one product: the 0–100 composite of Engagement, Discovery and
 * Sentiment, min–max normalised WITHIN the filtered sample.
 *
 * "Within the filtered sample" is why this is per-product and not sliced from a
 * job-wide number: normalising across all five departments and then splitting
 * would score every department against Brand's volume. Each department is
 * normalised against itself, which is the only reading that means anything to
 * the department looking at it.
 */
export function useSkyCulturalRelevance(
  product: string,
  enabled = true,
  labelQuery?: LabelQuery,
): UseSkyCulturalRelevanceResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])

  const queryKey = skyQueryKey(product, 'cultural_relevance', { labelsKey })
  const query = useQuery({
    queryKey,
    enabled: Boolean(product && account.user && enabled),
    staleTime: 30000,
    retry: 1,
    retryDelay: 1000,
    placeholderData: keepWhileOnlyFilterChanges<CulturalRelevanceResult>(queryKey),
    queryFn: async ({ signal }): Promise<CulturalRelevanceResult> => {
      try {
        const res = await axios.post<CulturalRelevanceResult>(
          skyProductURL(product, '/entities/cultural-relevance'),
          {
            entity: labelQuery?.entity,
            labelQuery,
            requireYes: MEANINGFUL_SENTIMENT_REQUIRE_YES,
          },
          { signal },
        )
        return res.data
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
  })

  return {
    culturalRelevance: query.data ?? null,
    isLoading: query.isLoading,
    isUpdating: query.isPlaceholderData,
    error: query.error as Error | null,
  }
}

export default useSkyCulturalRelevance
